import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import Icon from '@cobalt/react-icon';
import { Text } from '@cobalt/react-typography';
import Spinner from '@cobalt/react-spinner';

import { BgcType, CaseStatusEnum, genCaseStatusMap } from '../helper';

type SelectorProps = Record<'disabled' | 'selected', boolean> & BgcType;

interface CaseStatusSelectorProps {
  status: CaseStatusEnum;
  isOpen: boolean;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}

const Selector = styled(Flex)<SelectorProps>`
  width: 101px;
  height: 24px;
  background-color: ${(props) => (props.selected ? props.selectedBgc : props.defaultBgc)};
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  transition: background-color 0.5s ease;
  user-select: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${(props) =>
      props.disabled ? props.defaultBgc : props.selected ? props.selectedBgc : props.hoverBgc};
  }
`;

function CaseStatusSelector(props: CaseStatusSelectorProps) {
  const { status, isOpen, disabled, loading, onClick } = props;

  const [t] = useTranslation();

  const caseStatusMap = useMemo(() => genCaseStatusMap(t), [t]);
  const caseStatusObj = useMemo(() => caseStatusMap[status], [caseStatusMap, status]);

  return (
    <Selector
      data-testid="caseStatusSelector"
      alignY="center"
      paddingLeft="2"
      disabled={disabled || loading}
      selected={isOpen}
      defaultBgc={caseStatusObj.defaultBgc}
      hoverBgc={caseStatusObj.hoverBgc}
      selectedBgc={caseStatusObj.selectedBgc}
      onClick={onClick}
    >
      {/* Status icon */}
      <Flex
        style={{ borderRadius: '50%' }}
        alignX="center"
        alignY="center"
        width="16px"
        height="16px"
        backgroundColor="white"
      >
        <Icon
          style={{ transform: 'scale(0.5) translateY(-1px)' }}
          name={caseStatusObj.icon}
          color={caseStatusObj.color}
          size="small"
        />
      </Flex>

      {/* Text */}
      <Box style={{ flex: 1, overflowX: 'hidden' }} paddingLeft="1">
        <Text size="small" color="white" truncated>
          {caseStatusObj.text}
        </Text>
      </Box>

      {/* Suffix icon */}
      <Flex alignX="center" alignY="center" width="24px" height="24px">
        {/* Expand icon */}
        {!loading && <Icon name={isOpen ? 'expand_less' : 'expand_more'} color="white" size="tiny" />}

        {/* Loading icon */}
        {loading && (
          <Spinner data-testid="spinner" style={{ transform: 'scale(0.5)' }} size="small" color="mono-dark" />
        )}
      </Flex>
    </Selector>
  );
}

export default CaseStatusSelector;
